import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DownloadOutlined,
  FileTextOutlined,
  FilterOutlined,
  FormOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

import { Bento, Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Button, Card, CardBody, Input, Spinner } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import type { CauHoiKhai, DeXuatKhai, PhongVanResult, TraLoiPhongVan } from '../../types';
import { CHIP_HAU_QUA, MAU_HAU_QUA } from '../../constants-kho';
import {
  chonSanLuaChon,
  DANG_LAM_TRUOC,
  demChip,
  duyetDuocCua,
  duyetHangLoatDuoc,
  dungTraLoi,
  gopDeXuat,
  gopKho,
  HAU_QUA,
  theoBangCua,
  type DeXuatBoNho,
} from '../../nghiep-vu/khai-bao';
import { taiVe } from '../../nghiep-vu/tao-docx';
import { docMd, docYml, doiChieu, DUOI_TRA_LOI, taoMdLoi, taoYmlCauHoi, tenTepCauHoi } from '../../nghiep-vu/tep-tra-loi';
import { useGhiPhongVan, usePhamViHangLoat, usePhongVan } from '../../hooks/useKho';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { ChuDam } from './ChuDam';

/**
 * Phỏng vấn khai báo (port `KhaiBaoTab.tsx` native) — hỏi bằng LỰA CHỌN kèm số thật, thay vì bắt gõ từng ô.
 *
 * "Người dùng gọi cột `created_at` là gì?" bắt người ta NGHĨ RA một cái tên; đưa sẵn "Ngày tạo" kèm nút duyệt thì họ chỉ
 * phải NHẬN RA nó đúng hay sai. Nhưng gợi ý không bao giờ tự thành lời khai — xem `nghiep-vu/khai-bao.ts`.
 *
 * Máy chủ chỉ trả câu của MỘT bảng mỗi lượt, nên màn hình tự giữ KHO câu của những bảng đã mở — tích luỹ chứ không thay:
 * mở bảng A khai vài ô, sang bảng B rồi bấm Lưu thì phải gửi được cả phần ở A.
 */
export function KhaiBaoTab({
  sourceId,
  bangCanSoi,
  cotCanSoi,
  suaDuoc,
}: {
  sourceId: string;
  /** Bảng/cột vừa bấm từ một lời từ chối trong câu trả lời: tự bắt đầu, mở đúng bảng, đẩy câu của đúng cột lên đầu. */
  bangCanSoi?: string | null;
  cotCanSoi?: string | null;
  /** Quyền sửa nguồn — ghi lời khai. Thiếu thì ẩn các nút ghi. */
  suaDuoc: boolean;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();

  const hoi = usePhongVan(sourceId);
  const xuatQ = usePhongVan(sourceId);
  const tatCaQ = usePhongVan(sourceId);
  const phamViLoat = usePhamViHangLoat(sourceId);
  const ghi = useGhiPhongVan(sourceId);

  // Mặc định đã lọc ở hai mức gây hại — xem `DANG_LAM_TRUOC`.
  const [loc, setLoc] = useState<string | null>(DANG_LAM_TRUOC);
  const [bangMo, setBangMo] = useState<string | null>(null);
  const [dap, setDap] = useState<Record<string, string>>({});
  const [duyet, setDuyet] = useState<Record<string, boolean>>({});
  /** Nghĩa gõ cho TỪNG mã của câu nghĩa mã — câu trả lời là một ánh xạ, không phải một chuỗi. */
  const [dapCap, setDapCap] = useState<Record<string, Record<string, string>>>({});
  /** Đề xuất đọc từ tệp trả lời — sống trong bộ nhớ màn hình, không lưu: gợi ý chưa duyệt là bản nháp, không phải dữ liệu. */
  const [deXuat, setDeXuat] = useState<DeXuatBoNho>({});
  const [kho, setKho] = useState<Record<string, CauHoiKhai[]>>({});
  /** Kho đã có câu của MỌI bảng (sau "Duyệt tất cả") — nhãn nút mới đếm được. */
  const [daTaiHet, setDaTaiHet] = useState(false);
  /** Phản hồi thành công gần nhất — không đọc `hoi.data`, nó về `undefined` mỗi lần mutation chạy lại. */
  const [kq, setKq] = useState<PhongVanResult | undefined>(undefined);
  const [docTep, setDocTep] = useState(false);

  const hoiMay = (v: { bang?: string | null; hauQua?: string | null }) =>
    hoi.mutate(v, {
      onSuccess: (r) => {
        setKq(r);
        if (r.bang) {
          const b = r.bang;
          setKho((p) => ({ ...p, [b]: r.cauHoi }));
          setBangMo(b);
        }
      },
      onError: (e) => toast.error(t('troLyAiKhoKhai.khongSinhDuoc', { loi: moTaLoi(e) })),
    });

  /** Bắt đầu lại từ đầu: bỏ kho, bỏ bảng đang mở, hỏi lại theo bộ lọc hiện tại. */
  const hoiLai = (hq: string | null) => {
    setKho({});
    setDaTaiHet(false);
    setBangMo(null);
    hoiMay({ hauQua: hq });
  };

  /** Tải câu của một bảng nếu chưa có; đã mở rồi thì chỉ đổi chỗ xem — gọi lại là ném mất phần vừa gõ. */
  const moBang = (ten: string) => {
    setBangMo(ten);
    if (!kho[ten]) hoiMay({ bang: ten, hauQua: loc });
  };

  const tatCa = useMemo(() => gopKho(kho).map((q) => gopDeXuat(q, deXuat)), [kho, deXuat]);
  const theoBang = kq?.theoBang ?? [];

  // Tự bắt đầu khi người dùng tới từ một lời từ chối — bắt bấm thêm "Bắt đầu" là dựng lại đúng cái rào vừa dỡ.
  const daTuHoi = useRef(false);
  useEffect(() => {
    if (!bangCanSoi || !sourceId || daTuHoi.current) return;
    daTuHoi.current = true;
    hoiMay({ bang: bangCanSoi });
    // `hoiMay` đổi danh tính mỗi lần vẽ; cờ `daTuHoi` giữ cho hiệu ứng chạy đúng một lần.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bangCanSoi, sourceId]);

  // Bảng được trỏ tới thắng, nhưng chỉ khi nó CÓ trong danh sách (bảng đã khai đủ thì không có câu nào).
  const coBangCanSoi = Boolean(bangCanSoi && theoBang.some((x) => x.ten === bangCanSoi));
  const tenBang = bangMo ?? (coBangCanSoi ? (bangCanSoi ?? '') : (kq?.bang ?? theoBang[0]?.ten ?? ''));

  /** Câu của bảng đang mở, câu của ĐÚNG cột vừa bấm lên đầu. */
  const dang = useMemo(() => {
    const goc = tatCa.filter((q) => (q.bang || '') === tenBang);
    if (!cotCanSoi) return goc;
    const hop = (q: CauHoiKhai) => q.ma.endsWith(`.${cotCanSoi}`);
    return [...goc].sort((a, b) => Number(hop(b)) - Number(hop(a)));
  }, [tatCa, tenBang, cotCanSoi]);

  const traLoi: TraLoiPhongVan[] = useMemo(() => dungTraLoi(tatCa, dap, dapCap, duyet), [tatCa, dap, dapCap, duyet]);
  /** Câu đã trả lời trong phiên, chưa Lưu — trừ khỏi số của máy chủ, không thì con số đứng yên như làm không công. */
  const daTraLoi = useMemo(() => new Set(traLoi.map((x) => x.ma)), [traLoi]);
  const conThieu = Math.max(0, (kq?.conThieu ?? 0) - daTraLoi.size);

  const duyetDuoc = duyetDuocCua(dang, dap, dapCap, duyet);
  const duyetDuocTatCa = duyetDuocCua(tatCa, dap, dapCap, duyet);
  /** Câu CÓ gợi ý nhưng cố ý ngoài diện duyệt hàng loạt — nói ra, không thì con số nút nhỏ đi đọc thành công cụ hỏng. */
  const phaiGatTungCai = tatCa.filter((q) => Boolean(q.goiY) && !duyetHangLoatDuoc(q)).length;

  const duyetHet = (ds: CauHoiKhai[]) =>
    setDuyet((p) => {
      const n = { ...p };
      for (const q of ds) n[q.ma] = true;
      return n;
    });

  /** Bấm sẵn câu chọn-một mà tệp trả lời được; gộp câu vào kho vì Lưu chỉ gửi câu có trong kho. */
  const apChonSan = (goc: CauHoiKhai[], ds: DeXuatKhai[]) => {
    const chon = chonSanLuaChon(goc, ds, dap);
    const n = Object.keys(chon).length;
    if (!n) return 0;
    setKho((p) => ({ ...theoBangCua(goc), ...p }));
    setDaTaiHet(true);
    setDap((p) => ({ ...chon, ...p }));
    return n;
  };

  /**
   * Tệp trả lời đọc TẤT ĐỊNH, không qua mô hình. Chỉ nhận đúng hai định dạng của `tep-tra-loi.ts` — tệp khác bị từ chối
   * ngay thay vì gửi cho mô hình đoán (đoán sai một mã là một lời khai sai trông như đúng). Đối chiếu với MỌI câu còn
   * trống, vì tệp là tệp của cả nguồn.
   */
  const napTep = async (f: File) => {
    setDocTep(true);
    try {
      const duoi = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
      if (!DUOI_TRA_LOI.split(',').includes(duoi)) throw new Error(t('troLyAiKhoKhai.tepSaiDinhDang', { duoi: DUOI_TRA_LOI }));
      const van = await f.text();
      if (!van.trim()) throw new Error(t('troLyAiKhoKhai.tepRong'));
      const doc = duoi === '.md' ? docMd(van) : docYml(van);
      const toanBo = await tatCaQ.mutateAsync({ tatCa: true });
      const goc = toanBo.cauHoi.filter((q) => !daTraLoi.has(q.ma));
      const dc = doiChieu(doc.traLoi, goc);
      const viSao = t('troLyAiKhoKhai.tuTepTraLoi');
      const ds: DeXuatKhai[] = dc.hopLe.map((d) => ({ ...d, viSao }));
      setDeXuat((p) => {
        const n = { ...p };
        for (const d of ds) n[d.ma] = { giaTri: d.giaTri, viSao, cap: d.cap };
        return n;
      });
      const chonSan = apChonSan(goc, ds);
      const soLoi = dc.loi.length + doc.loi.length;
      if (!ds.length) toast.warning(t('troLyAiKhoKhai.tepKhongCoTraLoi'));
      else {
        toast.success(
          t('troLyAiKhoKhai.tepDaDoc', { n: soVN(ds.length, locale) }) +
            (chonSan ? ` ${t('troLyAiKhoKhai.moTaDaChonSan', { n: soVN(chonSan, locale) })}` : ''),
        );
      }
      if (soLoi) {
        toast.warning(t('troLyAiKhoKhai.tepCoLoi', { n: soVN(soLoi, locale) }));
        taiVe(
          new Blob([taoMdLoi(f.name, doc.loi, dc.loi)], { type: 'text/markdown;charset=utf-8' }),
          `loi-tep-tra-loi-${sourceId}-${new Date().toISOString().slice(0, 10)}.md`,
        );
      }
    } catch (e) {
      toast.error(t('troLyAiKhoKhai.moTaHong', { loi: moTaLoi(e) }));
    } finally {
      setDocTep(false);
    }
  };

  /** Tải MỌI câu lúc bấm rồi lọc phần đã trả lời — tệp chỉ có những bảng tình cờ đã mở là tệp nói sai việc còn phải khai. */
  const xuat = () =>
    xuatQ.mutate(
      { hauQua: loc, tatCa: true },
      {
        onSuccess: (r) => {
          const conLai = r.cauHoi.filter((q) => !daTraLoi.has(q.ma));
          taiVe(new Blob([taoYmlCauHoi(conLai, r.nhanBang, sourceId)], { type: 'text/yaml;charset=utf-8' }), tenTepCauHoi(sourceId));
        },
        onError: (e) => toast.error(t('troLyAiKhoKhai.xuatHong', { loi: moTaLoi(e) })),
      },
    );

  /** Duyệt gợi ý của MỌI bảng theo bộ lọc: tải hết rồi gộp vào kho; bảng đã mở giữ nguyên. Duyệt vẫn chưa ghi. */
  const duyetMoiBang = () =>
    tatCaQ.mutate(
      { hauQua: loc, tatCa: true },
      {
        onSuccess: (r) => {
          const gop = { ...theoBangCua(r.cauHoi), ...kho };
          setKho(gop);
          setDaTaiHet(true);
          const ds = duyetDuocCua(
            gopKho(gop).map((q) => gopDeXuat(q, deXuat)),
            dap,
            dapCap,
            duyet,
          );
          duyetHet(ds);
          if (ds.length) toast.success(t('troLyAiKhoKhai.daDuyetTatCa', { n: soVN(ds.length, locale) }));
          else toast.info(t('troLyAiKhoKhai.khongConGoiY'));
        },
        onError: (e) => toast.error(t('troLyAiKhoKhai.khongSinhDuoc', { loi: moTaLoi(e) })),
      },
    );

  const chayPhamViLoat = (gt: 'co' | 'khong') =>
    phamViLoat.mutate(gt, {
      onSuccess: (r) => {
        toast.success(t('troLyAiKhoKhai.phamViLoatXong', { n: soVN(r.daDoi, locale) }));
        // Bản khai vừa đổi ⇒ mọi bảng đã tải đều cũ, và chỉ mục bảng cũng đổi.
        hoiLai(loc);
      },
      onError: (e) => toast.error(t('troLyAiKhoKhai.phamViLoatHong', { loi: moTaLoi(e) })),
    });

  const luu = () =>
    ghi.mutate(traLoi, {
      onSuccess: (r) => {
        // Thuật ngữ bị đè là MẤT DỮ LIỆU — nói riêng. `boQua` là phần không ghi được — im lặng là mời gõ lại lần hai.
        if (r.deLen?.length) {
          toast.warning(t('troLyAiKhoKhai.daGhiCoDeLen', { n: soVN(r.deLen.length, locale), ds: r.deLen.join('; ') }));
        }
        if (r.boQua.length) {
          toast.warning(
            t('troLyAiKhoKhai.daGhiCoBoQua', {
              n: soVN(r.daGhi.length, locale),
              m: soVN(r.boQua.length, locale),
              ds: r.boQua.join(', '),
            }),
          );
        } else {
          toast.success(t('troLyAiKhoKhai.daGhi', { n: soVN(r.daGhi.length, locale) }));
        }
        setDap({});
        setDapCap({});
        setDuyet({});
        // Gợi ý đã ghi phải rời khỏi màn hình, không thì "Duyệt tất cả 40 gợi ý" hiện lại ngay sau khi vừa lưu 40 lời khai.
        setDeXuat((p) => {
          const n = { ...p };
          for (const ma of r.daGhi) delete n[ma];
          return n;
        });
        // Bỏ CẢ kho: một lời khai có thể làm câu ở bảng khác biến mất (thuật ngữ khai một lần, dùng nhiều bảng).
        setKho({});
        setDaTaiHet(false);
        hoiMay({ bang: tenBang, hauQua: loc });
      },
      onError: (e) => toast.error(t('troLyAiKhoKhai.khongGhiDuoc', { loi: moTaLoi(e) })),
    });

  return (
    <Bento>
      <Tile span="lg:col-span-12">
        <TileHeader
          icon={<FormOutlined />}
          tone="violet"
          title={t('troLyAiKhoKhai.tieuDe')}
          action={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* Phạm vi hàng loạt: ở SAP B1 ~2.800 bảng, trả lời tay câu ① là hai nghìn tám trăm lượt bấm chỉ để BẮT ĐẦU.
                  Cả hai nút CHỈ chạm bảng chưa ai trả lời — nhãn phải nói ra con số ấy. */}
              {suaDuoc && kq && kq.chuaXacNhan > 0 ? (
                <>
                  <Button variant="secondary" size="sm" loading={phamViLoat.isPending} onClick={() => chayPhamViLoat('co')}>
                    {t('troLyAiKhoKhai.phamViCoHet', { n: soVN(kq.chuaXacNhan, locale) })}
                  </Button>
                  <Button variant="ghost" size="sm" loading={phamViLoat.isPending} onClick={() => chayPhamViLoat('khong')}>
                    {t('troLyAiKhoKhai.phamViKhongHet', { n: soVN(kq.chuaXacNhan, locale) })}
                  </Button>
                </>
              ) : null}
              {/* Ba nút áp cho CẢ bộ câu hỏi, nên đứng ở khối chung — đặt trong thẻ của một bảng là khai sai phạm vi của nó. */}
              {kq && conThieu > 0 ? (
                <>
                  <Button variant="ghost" size="sm" icon={<DownloadOutlined />} loading={xuatQ.isPending} onClick={xuat}>
                    {t('troLyAiKhoKhai.xuatCauHoi', { n: soVN(conThieu, locale) })}
                  </Button>
                  <label
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-1.5 rounded border border-default px-2.5 py-1 text-xs font-medium focus-within:ring-2 focus-within:ring-primary-500',
                      docTep ? 'text-content-muted' : 'text-content-secondary hover:bg-subtle',
                    )}
                  >
                    <input
                      type="file"
                      className="sr-only"
                      accept={DUOI_TRA_LOI}
                      disabled={docTep}
                      aria-label={t('troLyAiKhoKhai.chonTep')}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        // Xoá value để chọn LẠI cùng một tệp vẫn kích hoạt — sửa tài liệu rồi tải lại là chuyện thường.
                        e.target.value = '';
                        if (f) void napTep(f);
                      }}
                    />
                    {docTep ? <Spinner size="small" /> : <FileTextOutlined aria-hidden />}
                    {docTep ? t('troLyAiKhoKhai.moTaDangDoc') : t('troLyAiKhoKhai.moTaTaiLenBo', { n: soVN(conThieu, locale) })}
                  </label>
                </>
              ) : null}
              {/* Duyệt tất cả: ở khối chung vì nó áp cho MỌI bảng — một tài liệu đề xuất cho hàng chục bảng cùng lúc. */}
              {kq && conThieu > 0 && !(daTaiHet && duyetDuocTatCa.length === 0) ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={tatCaQ.isPending && !docTep}
                  onClick={() => (daTaiHet ? duyetHet(duyetDuocTatCa) : duyetMoiBang())}
                >
                  {daTaiHet
                    ? t('troLyAiKhoKhai.duyetTatCa', { n: soVN(duyetDuocTatCa.length, locale) })
                    : t('troLyAiKhoKhai.duyetTatCaMoiBang')}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                loading={hoi.isPending}
                disabled={!sourceId}
                onClick={() => {
                  setLoc(null);
                  hoiLai(null);
                }}
              >
                {kq ? t('troLyAiKhoKhai.hoiLai') : t('troLyAiKhoKhai.batDau')}
              </Button>
            </div>
          }
        />
        <TileBody className="space-y-3">
          <p className="text-xs leading-relaxed text-content-secondary">
            <ChuDam chu={t('troLyAiKhoKhai.moTa')} />
          </p>

          {/* Còn bao nhiêu việc THEO MỨC THIỆT HẠI — ba mức không cùng đơn vị. Máy chủ đếm TRƯỚC khi lọc. */}
          {kq && kq.conThieu > 0 ? (
            <p className="text-xs leading-relaxed">
              <span className="font-medium text-danger-700">
                {t('troLyAiKhoKhai.tomTatSaiAmTham', { n: soVN(kq.theoHauQua.sai_am_tham ?? 0, locale) })}
              </span>
              {' · '}
              <span className="text-warning-700">
                {t('troLyAiKhoKhai.tomTatBiTuChoi', { n: soVN(kq.theoHauQua.bi_tu_choi ?? 0, locale) })}
              </span>
              {' · '}
              <span className="text-content-muted">
                {t('troLyAiKhoKhai.tomTatKhoDoc', { n: soVN(kq.theoHauQua.kho_doc ?? 0, locale) })}
              </span>
            </p>
          ) : null}

          {phaiGatTungCai > 0 ? (
            <p className="text-xs leading-relaxed text-content-muted">
              {t('troLyAiKhoKhai.phaiGatTungCai', { n: soVN(phaiGatTungCai, locale) })}
            </p>
          ) : null}

          {/* Hai câu dưới giữ số của MÁY CHỦ, không dùng `conThieu` — không thì trả lời xong câu cuối là báo "đã đủ" khi
              chưa ai bấm Lưu. Và `conThieu === 0` khi đang lọc không có nghĩa là bản khai đã đủ. */}
          {!kq ? (
            <p className="rounded bg-subtle px-3 py-2 text-sm text-content-secondary">
              <ChuDam chu={t('troLyAiKhoKhai.moiBatDau')} />
            </p>
          ) : kq.conThieu === 0 && !loc ? (
            <p className="rounded bg-success-50 px-3 py-2 text-sm text-success-700">{t('troLyAiKhoKhai.daDu')}</p>
          ) : kq.conThieu === 0 ? (
            <p className="rounded bg-subtle px-3 py-2 text-sm text-content-secondary">{t('troLyAiKhoKhai.locKhongCoCau')}</p>
          ) : null}
        </TileBody>
      </Tile>

      {/* Điều kiện là `kq`, không phải `kq.conThieu > 0`: chip lọc sống trong khối này, ẩn đi là nhốt người dùng với chip vừa bấm. */}
      {kq ? (
        <>
          <Tile span="lg:col-span-4">
            <TileHeader
              icon={<FilterOutlined />}
              tone="orange"
              title={t('troLyAiKhoKhai.locTieuDe')}
              mo={t('troLyAiKhoKhai.locMo')}
            />
            <TileBody className="space-y-3">
              <div className="flex flex-wrap items-center gap-1.5 text-xs" role="group">
                {HAU_QUA.map(([k, mau]) => {
                  const n = demChip(k, kq.theoHauQua, tatCa);
                  const chon = loc === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={!n}
                      aria-pressed={chon}
                      onClick={() => {
                        // Bộ lọc chạy ở MÁY CHỦ: đổi chip là đổi cả chỉ mục bảng — phải hỏi lại.
                        const moi = chon ? null : k;
                        setLoc(moi);
                        hoiLai(moi);
                      }}
                      className={cn(
                        'rounded-full px-2.5 py-1 transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                        chon ? CHIP_HAU_QUA[mau].chon : CHIP_HAU_QUA[mau].thuong,
                      )}
                    >
                      {t('troLyAiKhoKhai.chipHauQua', { n: soVN(n, locale), mo: t(`troLyAiKhoKhai.hauQua.${k}`) })}
                    </button>
                  );
                })}
              </div>

              <div className="flex max-h-[420px] flex-col gap-1.5 overflow-y-auto">
                {theoBang.map(({ ten, so }) => {
                  const chon = ten === tenBang;
                  return (
                    <button
                      key={ten}
                      type="button"
                      aria-current={chon ? 'true' : undefined}
                      onClick={() => moBang(ten)}
                      className={cn(
                        'flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-start text-xs transition-colors',
                        chon ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-default hover:bg-subtle',
                      )}
                    >
                      <span className="min-w-0 truncate font-medium">{kq.nhanBang[ten] ?? ten}</span>
                      <span className={cn('shrink-0 tabular-nums', chon ? 'text-primary-700' : 'text-content-muted')}>
                        {soVN(so, locale)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </TileBody>
          </Tile>

          <Tile span="lg:col-span-8">
            <TileHeader
              icon={<QuestionCircleOutlined />}
              tone="primary"
              title={tenBang ? (kq.nhanBang[tenBang] ?? tenBang) : t('troLyAiKhoKhai.cauCanKhai')}
              mo={t('troLyAiKhoKhai.goiYChuaDuyet')}
              action={
                suaDuoc ? (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {duyetDuoc.length > 0 ? (
                      <Button variant="secondary" size="sm" onClick={() => duyetHet(duyetDuoc)}>
                        {t('troLyAiKhoKhai.duyetCaBang', { n: soVN(duyetDuoc.length, locale) })}
                      </Button>
                    ) : null}
                    <Button size="sm" disabled={traLoi.length === 0} loading={ghi.isPending} onClick={luu}>
                      {traLoi.length > 0
                        ? t('troLyAiKhoKhai.luuSoLoiKhai', { n: soVN(traLoi.length, locale) })
                        : t('troLyAiKhoKhai.luu')}
                    </Button>
                  </div>
                ) : undefined
              }
            />
            <TileBody>
              {dang.length ? (
                <ul className="space-y-3">
                  {dang.map((q) => (
                    <CauHoi
                      key={q.ma}
                      q={q}
                      noiBat={Boolean(cotCanSoi) && q.ma.endsWith(`.${cotCanSoi}`)}
                      giaTri={dap[q.ma] ?? ''}
                      cap={dapCap[q.ma] ?? {}}
                      daDuyet={Boolean(duyet[q.ma])}
                      onSua={(v) => setDap((p) => ({ ...p, [q.ma]: v }))}
                      onSuaCap={(ma, v) => setDapCap((p) => ({ ...p, [q.ma]: { ...(p[q.ma] ?? {}), [ma]: v } }))}
                      onDuyet={() => setDuyet((p) => ({ ...p, [q.ma]: !p[q.ma] }))}
                    />
                  ))}
                </ul>
              ) : hoi.isPending ? (
                // Bảng chưa vào kho thì `dang` rỗng trong lúc chờ — đừng bảo "Chọn một bảng" đúng lúc vừa bấm một bảng.
                <p className="flex items-center justify-center gap-2 py-8 text-sm text-content-muted" aria-busy="true">
                  <Spinner size="small" /> {t('troLyAiKhoKhai.dangTaiBang')}
                </p>
              ) : (
                <p className="py-8 text-center text-sm text-content-muted">{t('troLyAiKhoKhai.chuaChonBang')}</p>
              )}
            </TileBody>
          </Tile>
        </>
      ) : null}
    </Bento>
  );
}

function CauHoi({
  q,
  giaTri,
  cap,
  daDuyet,
  noiBat,
  onSua,
  onSuaCap,
  onDuyet,
}: {
  q: CauHoiKhai;
  giaTri: string;
  cap: Record<string, string>;
  daDuyet: boolean;
  /** Câu người dùng vừa được dẫn tới từ một câu trả lời hỏng. */
  noiBat?: boolean;
  onSua: (v: string) => void;
  onSuaCap: (ma: string, v: string) => void;
  onDuyet: () => void;
}) {
  const { t } = useT();
  const dungGoiY = Boolean(q.goiY) && !giaTri.trim();
  const hien = giaTri || (dungGoiY ? (q.goiY?.giaTri ?? '') : '');
  const goiYCap = q.goiY?.cap ?? {};
  const dungGoiYCap = Object.keys(goiYCap).length > 0;
  const nutDuyet = (
    <Button variant={daDuyet ? 'ghost' : 'secondary'} size="sm" onClick={onDuyet}>
      {daDuyet ? t('troLyAiKhoKhai.daDuyet') : t('troLyAiKhoKhai.duyet')}
    </Button>
  );

  return (
    <li>
      <Card className={noiBat ? 'ring-2 ring-primary-400' : undefined}>
        <CardBody className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="min-w-0 flex-1 text-sm font-medium text-content-primary">{q.noiDung}</p>
            {/* Mức thiệt hại của TỪNG câu: quyết định người ta trả lời cẩn thận hay bấm cho xong. */}
            <span className={cn('shrink-0 text-xs', MAU_HAU_QUA[q.hauQua] ?? 'text-content-muted')}>
              {q.hauQua === 'sai_am_tham' || q.hauQua === 'bi_tu_choi' || q.hauQua === 'kho_doc'
                ? t(`troLyAiKhoKhai.hauQua.${q.hauQua}`)
                : q.hauQua}
            </span>
          </div>
          {q.viSao ? <p className="text-xs leading-relaxed text-content-secondary">{q.viSao}</p> : null}

          {/* Câu NGHĨA MÃ: mỗi mã một ô nghĩa — câu hỏi "mỗi mã nghĩa là gì" không trả lời được bằng cách chọn MỘT mã. */}
          {q.nhieuLuaChon ? (
            <div className="space-y-1.5">
              {q.luaChon.map((c) => {
                const daGo = cap[c.giaTri] ?? '';
                const goiYNghia = goiYCap[c.giaTri] ?? '';
                const dungGoiYO = !daGo.trim() && Boolean(goiYNghia);
                return (
                  <div key={c.giaTri} className="flex flex-wrap items-center gap-2">
                    <code className="w-24 shrink-0 truncate text-xs text-content-secondary">{c.giaTri}</code>
                    {c.bangChung ? <span className="w-16 shrink-0 text-xs text-content-muted">{c.bangChung}</span> : null}
                    <Input
                      aria-label={t('troLyAiKhoKhai.nghiaCuaMa', { cau: q.noiDung, ma: c.giaTri })}
                      size="small"
                      value={daGo || goiYNghia}
                      placeholder={t('troLyAiKhoKhai.nghiaPlaceholder')}
                      status={dungGoiYO && !daDuyet ? 'warning' : undefined}
                      className="min-w-0 flex-1"
                      onChange={(e) => onSuaCap(c.giaTri, e.target.value)}
                    />
                  </div>
                );
              })}
              {dungGoiYCap ? nutDuyet : null}
            </div>
          ) : q.luaChon.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {q.luaChon.map((c) => {
                const daChon = giaTri === c.giaTri;
                // Câu chỉ-chọn không có ô nhập: gợi ý phải hiện trên chính nút, không thì người ta duyệt thứ không đọc được.
                const laGoiY = dungGoiY && c.giaTri === q.goiY?.giaTri;
                return (
                  <button
                    key={c.giaTri}
                    type="button"
                    aria-pressed={daChon || laGoiY}
                    onClick={() => onSua(c.giaTri)}
                    className={cn(
                      'rounded-lg border px-2.5 py-1.5 text-start text-xs transition-colors',
                      daChon
                        ? 'border-primary-400 bg-primary-50 text-primary-700'
                        : laGoiY
                          ? 'border-warning-500 bg-warning-50 text-warning-700'
                          : 'border-default hover:bg-subtle',
                    )}
                  >
                    <span className="block font-medium">{c.nhan || c.giaTri}</span>
                    {/* Bằng chứng cạnh lựa chọn: "tổng cả bảng: 344.916.000" thì nhìn là biết có cộng dồn được không. */}
                    {c.bangChung ? (
                      <span className="mt-0.5 block font-mono text-xs text-content-muted">{c.bangChung}</span>
                    ) : null}
                  </button>
                );
              })}
              {dungGoiY && !q.traLoiTuDo ? nutDuyet : null}
            </div>
          ) : null}

          {q.traLoiTuDo && !q.nhieuLuaChon ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={q.noiDung}
                size="small"
                value={hien}
                placeholder={t('troLyAiKhoKhai.traLoiPlaceholder')}
                status={dungGoiY && !daDuyet ? 'warning' : undefined}
                className="min-w-0 flex-1"
                onChange={(e) => onSua(e.target.value)}
              />
              {dungGoiY ? nutDuyet : null}
            </div>
          ) : null}

          {/* Căn cứ của gợi ý — vẽ cả khi đã duyệt: người soát lại vẫn cần thấy phép đoán dựa vào đâu. */}
          {q.goiY?.viSao ? (
            <p className="text-xs text-content-muted">
              <span className="font-medium">{t('troLyAiKhoKhai.goiYCanCu')}</span> {q.goiY.viSao}
            </p>
          ) : null}

          {(q.nhieuLuaChon ? dungGoiYCap : dungGoiY) && !daDuyet ? (
            <p className="text-xs text-warning-700">{t('troLyAiKhoKhai.goiYNhan')}</p>
          ) : null}
        </CardBody>
      </Card>
    </li>
  );
}
